import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";

// One "fullscreen page" slot per portal. The provider mounts once in the layout,
// above both the navbar and the page Outlet. A page that wants a focused, chrome
// free view registers itself for as long as it is mounted, passing the back
// destination its header arrow should return to. That one registration drives two
// things depending on the breakpoint:
//
//   - Desktop: nothing changes about the chrome. The navbar stays, and the
//     PageHeader renders a back arrow just before the page title.
//   - Mobile:  the page goes fullscreen. The layout omits the entire navbar (top
//     bar, floating bottom tabs, and the launchpad it carries), and the
//     PageHeader's back arrow becomes the only navigation affordance.
//
// So registering a fullscreen page means "drop the navbar on mobile, and here is
// where the back arrow goes". A page that registers MUST render a PageHeader,
// since on mobile that arrow is its sole way back.
//
// Mirrors QuickBarContext: pages never render the chrome themselves, they only
// register intent. v2 routes are distinct lazyLoad fibers, so the outgoing page
// clears its registration before the incoming page registers.
const FullscreenPageContext = createContext(null);

export function FullscreenPageProvider({ children }) {
  // { backTo } when a page has gone fullscreen, else null.
  const [fullscreen, setFullscreen] = useState(null);

  const registerFullscreen = useCallback(
    (target) => setFullscreen(target ?? null),
    []
  );
  const clearFullscreen = useCallback(() => setFullscreen(null), []);

  const value = useMemo(
    () => ({ fullscreen, registerFullscreen, clearFullscreen }),
    [fullscreen, registerFullscreen, clearFullscreen]
  );

  return (
    <FullscreenPageContext.Provider value={value}>
      {children}
    </FullscreenPageContext.Provider>
  );
}

// Read the current fullscreen registration. Returns { backTo } or null. Tolerant
// of a missing provider: the navbar is bridged onto legacy pages with no provider,
// and the shared PageHeader is used by portals that don't mount one. It returns
// null there, so the navbar is never omitted and no back arrow shows.
export function useFullscreenPage() {
  const ctx = useContext(FullscreenPageContext);
  return ctx?.fullscreen ?? null;
}

// Declare this page fullscreen for as long as it is mounted. Pass a full SPA path
// string for the back arrow's destination (build it with the schoolPaths helpers).
// No-op when no provider is present. The dependency is the primitive `backTo`, so
// passing the same path across renders does not re-register.
export function useRegisterFullscreenPage(backTo) {
  const ctx = useContext(FullscreenPageContext);
  const registerFullscreen = ctx?.registerFullscreen;
  const clearFullscreen = ctx?.clearFullscreen;

  useEffect(() => {
    if (!registerFullscreen || !clearFullscreen) return undefined;
    registerFullscreen(backTo ? { backTo } : null);
    return () => clearFullscreen();
  }, [registerFullscreen, clearFullscreen, backTo]);
}
